// Step 2: the stencil as a sheet of paper. Black ink on white: a pen, a ruler that lays straight lines point by point
// (double-click to close and fill), an eraser, and a mirror pane over the right half. What is inked is what gets carved.

import type { Stencil, Stroke } from '../types';
import { StencilModel, handleFrame } from '../stencil/model';
import { pathsForStencil, reflectStroke, stencilToSVG, type Shape } from '../stencil/raster';
import { rdp, type Pt } from '../stencil/geom';
import { PRESETS } from '../stencil/presets';
import { el, button, toast, ghost, gooDrips } from '../ui/dom';
import { ICONS } from '../stencil/icons';
import { SYMBOLS, symbolOnSheet, symbolPath } from './symbols';
import { svgRegions } from '../stencil/svg';

type Tool = 'pen' | 'ruler' | 'shapes' | 'move' | 'eraser';

// sheet units, the line's full width; the slider moves the active tool's own
const SIZES = { pen: { min: 0.008, max: 0.07, value: 0.0225 }, eraser: { min: 0.02, max: 0.18, value: 0.07 } };
const HINTS: Record<Tool, string> = {
  pen: 'Draw freely.',
  ruler: 'Click to lay points. Double-click to close and fill. Click the first point to close too. Enter stops a line; Esc cancels.',
  shapes: 'Pick a symbol and it lands on the paper, ready to move.',
  move: 'Click a shape and drag it. The top handle turns it, a corner scales it. Delete removes it.',
  eraser: 'Rub out ink.',
};
const PAPER = '#eee7d6', INK = '#15110d', LEAD = 'rgba(70, 66, 62, 0.75)', SCRAPE = '#8b857c';
const SIZE = 1024;
const IC = (d: string) => `<svg viewBox="0 0 24 24" width="26" height="26" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${d}</svg>`;
const UI_ICONS = {
  back: IC('<path d="M10 6L4 12L10 18"/><path d="M4 12H20"/>'),
  undo: IC('<path d="M4 5V10H9"/><path d="M5.2 14.5A8 8 0 1 0 6.3 7.2L4 10"/>'),
  bin: IC('<path d="M4 7H20"/><path d="M9 7V4H15V7"/><path d="M6 7L7 20H17L18 7"/><path d="M10 11V16M14 11V16"/>'),
};
const DOUBLE = { ms: 420, near: 0.07 };

export interface SheetHooks { onCarve(): void }

export class Sheet {
  root: HTMLElement;
  /** the paper as drawn: the screen's sheet and the texture that wraps onto the pumpkin */
  canvas = el('canvas');
  private paper: HTMLElement;
  private mirror: HTMLElement;
  private hsvg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  private shapes!: HTMLElement;
  private band = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  private bandLines: SVGLineElement[] = [];
  private ui = el('div', 'sheet-ui');
  private lastClick = { t: 0, p: [9, 9] as Pt };
  private facesLabel = el('span');
  private tool: Tool = 'pen';
  /** whether Move (or a shape just dropped) is in hand, so the gourd shows the same handles */
  get moving() { return this.tool === 'move' || this.tool === 'shapes'; }
  private sizes = { pen: SIZES.pen.value, eraser: SIZES.eraser.value };
  private hint = el('p', 'sheet-hint');
  private size = el('input');
  private mirrorCb = el('input');
  private inkB!: HTMLElement;
  private off: (() => void)[] = [];
  private grain = grainPattern();
  private drawing = false;
  private dlg: HTMLDialogElement | null = null;

  constructor(host: HTMLElement, public model: StencilModel, private hooks: SheetHooks) {
    this.canvas.width = this.canvas.height = SIZE;
    this.root = el('section', 'sheet');
    const head = el('h2', 'sheet-h', 'Draw your stencil');
    const desk = el('div', 'sheet-desk');
    this.paper = el('div', 'sheet-paper');
    this.mirror = el('div', 'sheet-mirror');
    this.mirror.setAttribute('aria-hidden', 'true');
    this.paper.append(this.canvas, this.mirror, this.rubber(), this.handles(), this.brushSvg(), this.sizer(), this.mirrorBits(), this.inkSwitch());
    desk.append(this.toolRail(), this.paper);
    this.root.append(head, this.hint, desk, this.foot());
    this.shapes = this.table();
    this.ui.append(...this.chrome(), this.shapes);
    host.append(this.root);
    host.parentElement!.append(this.ui);

    model.setTool('saw');
    model.setOptions({ mirror: false, loops: false, snap: false });
    this.off.push(model.onChange(r => { if (r === 'stencil' || r === 'draft') this.draw(); if (r === 'stencil') this.sync(); if (r === 'selection' || r === 'draft') this.drawHandles(); }));
    this.pointer();
    // an SVG file dropped on the paper lands on it as ink (no button yet)
    this.paper.addEventListener('dragover', e => { if (e.dataTransfer?.types.includes('Files')) e.preventDefault(); });
    this.paper.addEventListener('drop', e => {
      const f = e.dataTransfer?.files[0];
      if (!f) return;
      e.preventDefault();
      if (f.type === 'image/svg+xml' || /\.svg$/i.test(f.name)) f.text().then(t => this.importSvg(t)); else toast('Only SVG files for now');
    });
    const key = (e: KeyboardEvent) => this.key(e);
    document.addEventListener('keydown', key);
    this.off.push(() => document.removeEventListener('keydown', key));
    const fit = () => this.fitTable();
    addEventListener('resize', fit);
    this.off.push(() => removeEventListener('resize', fit));
    this.draw();
    this.sync();
    this.root.classList.add('in');
  }

  dispose() {
    this.off.forEach(f => f());
    this.root.remove();
    this.ui.remove();
    this.dlg?.remove();
  }

  /** the paper and its controls fade while the paper flies onto the gourd */
  leave() { this.root.classList.add('leaving'); this.ui.classList.add('leaving'); }

  // ---- the controls

  private toolRail(): HTMLElement {
    const rail = el('div', 'sheet-tools');
    const tools: [Tool, string][] = [['pen', 'Pen'], ['ruler', 'Ruler'], ['shapes', 'Shapes'], ['move', 'Move'], ['eraser', 'Eraser']];
    for (const [t, label] of tools) {
      const b = button('', () => this.pick(t), 'sheet-tool piece');
      b.dataset.tool = t;
      b.title = label;
      b.setAttribute('aria-label', label);
      b.innerHTML = `${ICONS[t]}<span>${label}</span>`;
      rail.append(b);
    }
    return rail;
  }

  /** the size slider, top right of the paper: pen width, or eraser width when the eraser is in hand; hidden for the ruler */
  private sizer(): HTMLElement {
    const box = el('label', 'sheet-size');
    box.append(el('span', '', 'Pen size'));
    Object.assign(this.size, { type: 'range', step: '0.001' });
    this.size.addEventListener('input', () => { this.sizes[this.tool === 'eraser' ? 'eraser' : 'pen'] = +this.size.value; });
    box.append(this.size);
    return box;
  }

  private pick(t: Tool) {
    this.model.setTool(t === 'pen' ? (this.model.ink === 'white' ? 'eraser' : 'saw') : t === 'shapes' ? 'move' : t);
    this.tool = t;
    this.bandTo(null);
    this.sync();
  }

  /** the table of symbols that takes the gourd's place on the left */
  private table(): HTMLElement {
    const t = el('div', 'shapes');
    t.hidden = true;
    t.append(el('h3', 'shapes-h', 'Shapes'));
    const grid = el('div', 'shapes-grid');
    for (const s of SYMBOLS) {
      const b = button(`<svg viewBox="0 0 100 100" aria-hidden="true"><path d="${symbolPath(s)}" fill="#15110d" fill-rule="nonzero"/></svg><span>${s.label}</span>`, () => this.drop(s), 'shape-b');
      b.dataset.shape = s.key;
      b.setAttribute('aria-label', s.label);
      this.dragOn(b, s);
      grid.append(b);
    }
    t.append(grid);
    return t;
  }

  /** on a phone the table lies over the top of the screen: it stops short of the tools and scrolls, so they and the paper stay in reach */
  private fitTable() {
    const t = this.shapes, rail = this.root.querySelector('.sheet-tools');
    t.style.maxHeight = '';
    if (t.hidden || !rail || !matchMedia('(max-aspect-ratio: 10/11)').matches) return;
    t.style.maxHeight = `${Math.max(120, rail.getBoundingClientRect().top - t.getBoundingClientRect().top - 8)}px`;
  }

  /** a symbol can also be dragged from the table and let go on the paper, where it lands under the pointer */
  private dragOn(b: HTMLElement, s: (typeof SYMBOLS)[number]) {
    let from: { x: number; y: number; id: number } | null = null, held: HTMLElement | null = null, dragged = false;
    b.style.touchAction = 'none';
    b.addEventListener('pointerdown', e => { from = { x: e.clientX, y: e.clientY, id: e.pointerId }; dragged = false; b.setPointerCapture(e.pointerId); });
    b.addEventListener('pointermove', e => {
      if (!from || e.pointerId !== from.id) return;
      if (!held && Math.hypot(e.clientX - from.x, e.clientY - from.y) < 6) return;
      if (!held) {
        held = el('div', 'shape-held', b.querySelector('svg')!.outerHTML);
        document.body.append(held);
      }
      held.style.translate = `${e.clientX}px ${e.clientY}px`;
    });
    const end = (e: PointerEvent, ok: boolean) => {
      if (!from || e.pointerId !== from.id) return;
      from = null;
      if (!held) return;
      held.remove();
      held = null;
      dragged = true;
      const r = this.canvas.getBoundingClientRect();
      if (!ok || e.clientX < r.left || e.clientX > r.right || e.clientY < r.top || e.clientY > r.bottom) return;
      this.model.addFill(symbolOnSheet(s, 0.5, this.at(e)), true);
      this.pick('move');
    };
    b.addEventListener('pointerup', e => end(e, true));
    b.addEventListener('pointercancel', e => end(e, false));
    // the click that follows a drag is not a second drop
    b.addEventListener('click', e => { if (dragged) { dragged = false; e.stopImmediatePropagation(); } }, true);
  }

  /** an SVG's ink onto the paper, fitted into the face's area */
  async importSvg(text: string) {
    try { this.model.addRegions(await svgRegions(text)); } catch (e) { toast((e as Error).message); }
  }

  private dropped = 0;
  private drop(s: (typeof SYMBOLS)[number]) {
    const k = this.dropped++ % 4, x = this.model.stencil.mirror ? -0.42 : 0;
    this.model.addFill(symbolOnSheet(s, 0.5, [x + k * 0.07, 0.1 - k * 0.07]), true);
    this.pick('move');
  }

  /** pen and eraser: a circle the size of the nib under the pointer, a pale rim under a dark one so it shows on ink */
  private bsvg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  private brushSvg(): SVGElement {
    const g = this.bsvg;
    g.setAttribute('viewBox', '-1 -1 2 2');
    g.setAttribute('preserveAspectRatio', 'none');
    g.setAttribute('class', 'sheet-handles sheet-brush');
    g.setAttribute('aria-hidden', 'true');
    g.innerHTML = '<ellipse class="nib-rim"/><ellipse class="nib"/>';
    return g;
  }

  private brushAt(p: Pt | null) {
    const on = !!p && (this.tool === 'pen' || this.tool === 'eraser');
    this.bsvg.style.display = on ? '' : 'none';
    if (!on) return;
    const r = String((this.tool === 'eraser' ? this.sizes.eraser : this.sizes.pen) / 2);
    for (const e of this.bsvg.children) {
      e.setAttribute('cx', String(p![0]));
      e.setAttribute('cy', String(-p![1]));
      e.setAttribute('rx', r);
      e.setAttribute('ry', r);
      e.classList.toggle('rub', this.tool === 'eraser');
    }
  }

  /** the selection's box, corner squares and turn handle, over the paper while the move tool is in hand */
  private handles(): SVGElement {
    const g = this.hsvg;
    g.setAttribute('viewBox', '-1 -1 2 2');
    g.setAttribute('class', 'sheet-handles');
    g.setAttribute('aria-hidden', 'true');
    return g;
  }

  private drawHandles() {
    const g = this.hsvg, h = this.tool === 'move' || this.tool === 'shapes' ? this.model.handles() : null;
    if (!h) { g.innerHTML = ''; return; }
    const q = (p: Pt) => `${p[0].toFixed(4)},${(-p[1]).toFixed(4)}`, c = 0.034;
    // corner squares turn with the box
    const { ux, uy, stalk } = handleFrame(h);
    const sq = (p: Pt) => `<path class="hs" d="M${[[-1, -1], [1, -1], [1, 1], [-1, 1]].map(([u, v]) => q([p[0] + (u * ux[0] + v * uy[0]) * c, p[1] + (u * ux[1] + v * uy[1]) * c])).join('L')}Z"/>`;
    g.innerHTML = `<path class="hb" d="M${q(h.nw)}L${q(h.ne)}L${q(h.se)}L${q(h.sw)}Z"/><path class="hb" d="M${q(stalk)}L${q(h.rotate)}"/>`
      + sq(h.nw) + sq(h.ne) + sq(h.sw) + sq(h.se) + `<circle class="hs" cx="${h.rotate[0]}" cy="${-h.rotate[1]}" r="${c * 1.25}"/>`;
  }

  private key(e: KeyboardEvent) {
    const t = e.target as HTMLElement | null;
    if (t && /^(INPUT|SELECT|TEXTAREA)$/.test(t.tagName) && !(t as HTMLInputElement).type?.match(/^(range|checkbox)$/)) return;
    if ((e.metaKey || e.ctrlKey) && !e.altKey && e.key.toLowerCase() === 'z') {
      e.preventDefault();
      if (this.model.busy) this.model.cancel(); else if (!e.shiftKey) this.model.undo();
      return;
    }
    if (this.tool === 'move' && (e.key === 'Delete' || e.key === 'Backspace') && this.model.selection) { e.preventDefault(); this.model.deleteSelection(); return; }
    if (!this.model.ruling) return;
    if (e.key === 'Enter') { e.preventDefault(); this.model.rulerFinish(); }
    else if (e.key === 'Escape') { e.preventDefault(); this.model.cancel(); }
  }

  /** the dotted rubber band from the last ruler point to the pointer */
  private rubber(): SVGElement {
    const b = this.band;
    b.setAttribute('viewBox', '-1 -1 2 2');
    b.setAttribute('preserveAspectRatio', 'none');
    b.setAttribute('class', 'sheet-band');
    b.setAttribute('aria-hidden', 'true');
    for (const cls of ['under', 'ants']) {
      const l = document.createElementNS('http://www.w3.org/2000/svg', 'line');
      l.setAttribute('class', cls);
      this.bandLines.push(l);
      b.append(l);
    }
    b.style.display = 'none';
    return b;
  }

  private bandTo(p: Pt | null) {
    const m = this.model;
    if (!p || !m.ruling) { this.band.style.display = 'none'; return; }
    const a = this.lastClick.p;
    for (const l of this.bandLines) {
      l.setAttribute('x1', String(a[0])); l.setAttribute('y1', String(-a[1]));
      l.setAttribute('x2', String(p[0])); l.setAttribute('y2', String(-p[1]));
    }
    this.band.style.display = '';
  }

  /** Mirror mode: a checkbox; the pane still flies off and back */
  private mirrorBits(): HTMLElement {
    const w = el('label', 'sheet-mirror-cb');
    this.mirrorCb.type = 'checkbox';
    this.mirrorCb.addEventListener('change', () => { if (this.mirrorCb.checked !== !!this.model.stencil.mirror) this.toggleMirror(); });
    w.append(this.mirrorCb, el('span', '', 'Mirror mode'));
    return w;
  }

  /** Black or White: white ink is paper again, so the pen, the ruler and the shapes cut out of what is black */
  private inkSwitch(): HTMLElement {
    const b = button('', () => this.setInk(this.model.ink === 'white' ? 'black' : 'white'), 'sheet-ink');
    b.setAttribute('role', 'switch');
    b.setAttribute('aria-label', 'White ink');
    b.innerHTML = '<span>Black</span><i></i><span>White</span>';
    this.inkB = b;
    return b;
  }

  setInk(ink: 'black' | 'white') {
    this.model.ink = ink;
    if (this.tool === 'pen') this.model.setTool(ink === 'white' ? 'eraser' : 'saw');
    this.sync();
  }

  private faceName(): string {
    const n = this.model.stencil.name, p = PRESETS.find(x => x.label === n);
    return p ? faceLabel(p.label) : 'Start from a face';
  }

  /** everything round the gourd on the left: the face picker, print and load, undo and clear (Back is in the app's top bar) */
  private chrome(): HTMLElement[] {
    const faces = el('div', 'faces');
    const fb = button('', () => { list.hidden = !list.hidden; fb.setAttribute('aria-expanded', String(!list.hidden)); }, 'faces-b');
    fb.setAttribute('aria-haspopup', 'listbox');
    fb.setAttribute('aria-expanded', 'false');
    fb.append(this.facesLabel, el('i', 'faces-caret'));
    const list = el('div', 'faces-list');
    list.hidden = true;
    list.setAttribute('role', 'listbox');
    for (const p of PRESETS) {
      const o = button(faceLabel(p.label), () => {
        list.hidden = true;
        fb.setAttribute('aria-expanded', 'false');
        this.model.loadStencil({ ...p.stencil, mirror: true, cut: false, carve: this.model.stencil.carve ?? [] });
      }, 'faces-o');
      o.dataset.key = p.key;
      o.setAttribute('role', 'option');
      list.append(o);
    }
    faces.append(fb, list);
    const away = (e: Event) => { if (!faces.contains(e.target as Node)) { list.hidden = true; fb.setAttribute('aria-expanded', 'false'); } };
    document.addEventListener('pointerdown', away);
    this.off.push(() => document.removeEventListener('pointerdown', away));

    const file = el('input');
    Object.assign(file, { type: 'file', accept: '.svg,image/svg+xml', hidden: true });
    file.addEventListener('change', async () => {
      const f0 = file.files?.[0];
      file.value = '';
      if (!f0) return;
      await this.importSvg(await f0.text());
    });
    const files = el('div', 'sheet-files');
    // POTENTIAL: Load SVG comes back as a button (Robin, round 7: "get rid of load SVG at the moment"); the input still works
    files.append(button('Print design', () => this.printDialog(), 'purp sheet-print'), file);

    const edit = el('div', 'sheet-edit');
    // the word to the left of the icon, so nobody has to guess what the arrow and the bin do
    const ib = (icon: string, word: string, f: () => void) => button(`<span>${word}</span>${icon}`, f, 'sheet-ib purp');
    edit.append(
      ib(UI_ICONS.undo, 'Undo', () => (this.model.busy ? this.model.cancel() : this.model.undo())),
      ib(UI_ICONS.bin, 'Clear', () => { this.model.cancel(); this.model.clear(); }),
    );
    return [faces, files, edit];
  }

  private printDialog() {
    if (!this.dlg) {
      const d = el('dialog', 'purple print-dlg');
      d.append(
        el('h2', 'print-h', 'Create your own pumpkin design'),
        el('p', 'print-p', 'Print out this design onto a sheet of A4 paper, or larger if you can. Use that to create your own real life Halloween pumpkin.'),
      );
      const row = el('div', 'row');
      row.append(
        button('Print', () => printSVG(stencilToSVG(this.model.stencil, { sheet: true })), 'goo small print-go'),
        button('Download SVG', () => save('pumpkin-stencil.svg', stencilToSVG(this.model.stencil, { sheet: true })), 'btn'),
        button('Close', () => d.close(), 'btn'),
      );
      d.append(row);
      d.addEventListener('click', e => { if (e.target === d) d.close(); });
      document.body.append(d);
      this.dlg = d;
    }
    this.dlg.showModal();
  }

  private foot(): HTMLElement {
    const f = el('div', 'sheet-foot');
    f.append(button('Start carving', () => { this.model.rulerFinish(); this.hooks.onCarve(); }, 'goo sheet-carve'));
    gooDrips(f.firstElementChild as HTMLElement);
    return f;
  }

  private sync() {
    this.root.querySelectorAll<HTMLElement>('.sheet-tool').forEach(b => {
      const on = b.dataset.tool === this.tool;
      b.classList.toggle('on', on);
      if (on && !b.querySelector('.ghost')) b.append(ghost());
      else if (!on) b.querySelector('.ghost')?.remove();
    });
    const sz = SIZES[this.tool === 'eraser' ? 'eraser' : 'pen'];
    Object.assign(this.size, { min: String(sz.min), max: String(sz.max), value: String(this.sizes[this.tool === 'eraser' ? 'eraser' : 'pen']) });
    this.hint.textContent = HINTS[this.tool];
    const on = !!this.model.stencil.mirror;
    this.root.classList.toggle('mirrored', on);
    this.mirrorCb.checked = on;
    this.paper.dataset.tool = this.tool;
    const white = this.model.ink === 'white';
    this.inkB.setAttribute('aria-checked', String(white));
    this.inkB.classList.toggle('white', white);
    this.paper.classList.toggle('white-ink', white);
    this.shapes.classList.toggle('white', white);
    this.shapes.hidden = this.tool !== 'shapes';
    this.ui.classList.toggle('shapes-on', this.tool === 'shapes');
    this.fitTable();
    this.drawHandles();
    this.facesLabel.textContent = this.faceName();
  }

  /** the mirror flies off the page and the reflection stays as ink; put back, it flies in and the right half follows the left again */
  private toggleMirror() {
    const on = !this.model.stencil.mirror;
    this.mirror.classList.remove('fly-in', 'fly-off');
    void this.mirror.offsetWidth;
    this.mirror.classList.add(on ? 'fly-in' : 'fly-off');
    this.model.setMirror(on);
  }

  // ---- drawing on it

  private at(e: PointerEvent): Pt {
    const r = this.canvas.getBoundingClientRect();
    return [((e.clientX - r.left) / r.width) * 2 - 1, 1 - ((e.clientY - r.top) / r.height) * 2];
  }

  private pointer() {
    const c = this.canvas, m = this.model;
    c.style.touchAction = 'none';
    const clamp = (p: Pt): Pt => [Math.max(-1, Math.min(1, p[0])), Math.max(-1, Math.min(1, p[1]))];
    const pr = (e: PointerEvent) => (e.pointerType === 'pen' ? e.pressure : 0.5);
    c.addEventListener('pointerdown', e => {
      if (e.button !== 0 && e.pointerType === 'mouse') return;
      if (this.press(clamp(this.at(e)), pr(e))) c.setPointerCapture(e.pointerId);
    });
    c.addEventListener('pointermove', e => { const p = clamp(this.at(e)); this.brushAt(p); this.drag(p, pr(e)); });
    c.addEventListener('pointerleave', () => { this.bandTo(null); this.brushAt(null); });
    c.addEventListener('pointerup', () => this.release(false));
    c.addEventListener('pointercancel', () => this.release(true));
  }

  /** the same gestures, from the gourd on the left: the caller maps the pointer on the skin to sheet coordinates */
  /** reach widens what a press can grab: the gourd is smaller on screen than the paper */
  press(p: Pt, pressure = 0.5, reach = 1): boolean {
    const m = this.model;
    m.setOptions({ width: this.tool === 'eraser' ? this.sizes.eraser : this.sizes.pen, mirror: false, loops: false });
    if (this.tool === 'ruler') { this.rulerClick(p); return false; }
    const r = m.hitRadius;
    m.hitRadius = r * reach;
    // a handle is taken within about 22px of it on the paper (the gourd passes its own reach)
    m.handleRadius = (44 / Math.max(1, this.canvas.getBoundingClientRect().width)) * reach;
    if (this.tool === 'move') m.prepareMove(p[0], p[1]);
    m.down(p[0], p[1], { pressure });
    m.hitRadius = r;
    this.drawing = true;
    return true;
  }

  drag(p: Pt | null, pressure = 0.5) {
    if (this.tool === 'ruler') { this.bandTo(p); return; }
    if (this.drawing && p) this.model.move(p[0], p[1], { pressure });
  }

  release(cancel: boolean) {
    if (!this.drawing) return;
    this.drawing = false;
    if (cancel) this.model.cancel(); else this.model.up();
  }

  /** a second click close by, soon after the first, is the double-click: close and fill, with no stray point from its second half */
  private rulerClick(p: Pt) {
    const m = this.model, now = performance.now(), l = this.lastClick;
    if (now - l.t < DOUBLE.ms && Math.hypot(p[0] - l.p[0], p[1] - l.p[1]) < DOUBLE.near) {
      l.t = now;
      if (m.ruling) m.rulerFinish(true);
      return;
    }
    m.down(p[0], p[1]);
    this.lastClick = { t: now, p };
    this.bandTo(m.ruling ? p : null);
  }

  // ---- the paper

  draw() {
    this.bandTo(null);
    const ctx = this.canvas.getContext('2d')!, n = SIZE, st = this.model.stencil;
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.globalCompositeOperation = 'source-over';
    ctx.fillStyle = PAPER;
    ctx.fillRect(0, 0, n, n);
    ctx.fillStyle = this.grain;
    ctx.fillRect(0, 0, n, n);
    // the fold down the middle
    ctx.strokeStyle = st.mirror ? 'rgba(40, 60, 80, 0.28)' : 'rgba(0, 0, 0, 0.06)';
    ctx.lineWidth = 2;
    ctx.setLineDash(st.mirror ? [10, 8] : []);
    ctx.beginPath(); ctx.moveTo(n / 2, 18); ctx.lineTo(n / 2, n - 18); ctx.stroke();
    ctx.setLineDash([]);
    const ink = inkLayer(n);
    paintInk(ink.getContext('2d')!, st, n, this.model.draft, !!st.mirror);
    ctx.drawImage(ink, 0, 0);
  }
}

// ---- ink

let inkCanvas: HTMLCanvasElement | null = null;
function inkLayer(n: number): HTMLCanvasElement {
  if (!inkCanvas || inkCanvas.width !== n) { inkCanvas = el('canvas'); inkCanvas.width = inkCanvas.height = n; }
  return inkCanvas;
}

function trace(ctx: CanvasRenderingContext2D, s: Shape) {
  ctx.beginPath();
  for (const poly of s.polys) {
    poly.forEach(([x, y], i) => (i ? ctx.lineTo(x, y) : ctx.moveTo(x, y)));
    if (!s.line) ctx.closePath();
  }
}

/** the stencil's marks in sheet space, ink and pencil, the eraser taking both back to paper; `draft` is what's being drawn */
function paintInk(ctx: CanvasRenderingContext2D, st: Stencil, n: number, draft: Stroke[] = [], mirror = false) {
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  ctx.globalCompositeOperation = 'source-over';
  ctx.clearRect(0, 0, n, n);
  ctx.setTransform(n / 2, 0, 0, -n / 2, n / 2, n / 2);
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  const live = mirror ? draft.flatMap(s => [s, reflectStroke(s)]) : draft;
  // the stored strokes go through the mirror; the draft is drawn as it is and reflected, wherever it is
  const stored = pathsForStencil(st, { pencil: true }), extra = pathsForStencil({ strokes: live, parts: [] }, { pencil: true });
  for (const s of [...stored, ...extra]) {
    const colour = s.tool === 'pencil' ? LEAD : s.tool === 'scraper' ? SCRAPE : INK;
    ctx.globalCompositeOperation = s.tool === 'eraser' ? 'destination-out' : 'source-over';
    trace(ctx, s);
    if (s.line) { ctx.strokeStyle = s.tool === 'eraser' ? '#000' : colour; ctx.lineWidth = s.width; ctx.stroke(); }
    else { ctx.fillStyle = colour; ctx.fill('nonzero'); }
  }
  ctx.globalCompositeOperation = 'source-over';
  ctx.setTransform(1, 0, 0, 1, 0, 0);
}

function grainPattern(): CanvasPattern | string {
  const c = el('canvas');
  c.width = c.height = 128;
  const x = c.getContext('2d');
  if (!x) return 'transparent';
  const img = x.createImageData(128, 128);
  let s = 7;
  for (let i = 0; i < img.data.length; i += 4) {
    s = (s * 16807) % 2147483647;
    const v = (s % 1000) / 1000;
    img.data[i] = img.data[i + 1] = img.data[i + 2] = v < 0.5 ? 0 : 255;
    img.data[i + 3] = Math.round(Math.abs(v - 0.5) * 22);
  }
  x.putImageData(img, 0, 0);
  return x.createPattern(c, 'repeat') ?? 'transparent';
}

// ---- fill: flood the paper from a point, then walk round the region's edge

const FILL_N = 320;

export function fillRegion(st: Stencil, x: number, y: number, N = FILL_N): Pt[] | 'ink' | 'open' {
  const c = el('canvas');
  c.width = c.height = N;
  const ctx = c.getContext('2d', { willReadFrequently: true })!;
  paintInk(ctx, st, N, [], false);
  const a = ctx.getImageData(0, 0, N, N).data;
  const wall = (i: number) => a[i * 4 + 3] > 90;
  const px = Math.floor((x * 0.5 + 0.5) * N), py = Math.floor((0.5 - y * 0.5) * N);
  if (px < 0 || py < 0 || px >= N || py >= N) return 'open';
  if (wall(py * N + px)) return 'ink';
  const R = new Uint8Array(N * N), stack = [py * N + px];
  R[py * N + px] = 1;
  while (stack.length) {
    const i = stack.pop()!, ix = i % N, iy = (i - ix) / N;
    if (ix === 0 || iy === 0 || ix === N - 1 || iy === N - 1) return 'open';
    for (const j of [i - 1, i + 1, i - N, i + N]) if (!R[j] && !wall(j)) { R[j] = 1; stack.push(j); }
  }
  // grow it a pixel so the fill tucks under the lines round it
  const G = R.slice();
  for (let i = N; i < N * N - N; i++) if (!R[i] && (R[i - 1] || R[i + 1] || R[i - N] || R[i + N])) G[i] = 1;
  const ring = traceRegion(G, N);
  const pts = rdp(ring.map(([vx, vy]) => [vx / N * 2 - 1, 1 - vy / N * 2] as Pt), 1.2 / N);
  return pts.length >= 3 ? pts : 'ink';
}

/** the outer edge of a region, walking the pixel cracks with the region on the right */
export function traceRegion(R: Uint8Array, N: number): Pt[] {
  const inside = (x: number, y: number) => x >= 0 && y >= 0 && x < N && y < N && R[y * N + x] === 1;
  const s = R.indexOf(1);
  if (s < 0) return [];
  const sx = s % N, sy = (s - sx) / N;
  let x = sx, y = sy, dx = 1, dy = 0;
  const pts: Pt[] = [];
  do {
    pts.push([x, y]);
    x += dx; y += dy;
    let left: boolean, right: boolean;
    if (dx === 1) { left = inside(x, y - 1); right = inside(x, y); }
    else if (dy === 1) { left = inside(x, y); right = inside(x - 1, y); }
    else if (dx === -1) { left = inside(x - 1, y); right = inside(x - 1, y - 1); }
    else { left = inside(x - 1, y - 1); right = inside(x, y - 1); }
    if (left) [dx, dy] = [dy, -dx];
    else if (!right) [dx, dy] = [-dy, dx];
  } while (!(x === sx && y === sy && dx === 1 && dy === 0) && pts.length < 4 * N * N);
  return pts;
}

// ---- SVG out

const faceLabel = (l: string) => (l === 'Classic' ? 'Classic pumpkin design' : l);

/** print only the stencil: a hidden frame holding the SVG, printed full page */
function printSVG(svg: string) {
  const f = el('iframe');
  f.style.cssText = 'position:fixed;left:-9999px;top:0;width:0;height:0;border:0';
  f.srcdoc = `<!doctype html><title>Pumpkin stencil</title><style>@page{margin:8mm}html,body{margin:0;background:#fff}svg{display:block;width:100%;height:auto;max-height:100vh}</style>${svg}`;
  f.onload = () => {
    const w = f.contentWindow!;
    w.addEventListener('afterprint', () => f.remove());
    w.focus();
    w.print();
    setTimeout(() => f.remove(), 60000);
  };
  document.body.append(f);
}

function save(name: string, text: string) {
  const a = el('a');
  a.href = URL.createObjectURL(new Blob([text], { type: 'image/svg+xml' }));
  a.download = name;
  a.click();
  setTimeout(() => URL.revokeObjectURL(a.href), 1000);
}
